/**
 * Owner: Masa Bayu
 * File: LandingInstallBanner.tsx
 * Date:Time: 2026-10-18 13:10
 * Description: Home landing §380 — notifikasi pasang: muncul bila QX dibuka
 *   dalam pelayar (promptable/Android atau a2hs/iOS), auto-hilang apabila
 *   sudah dipasang atau berjalan standalone; "Nanti" persist ke localStorage.
 */

import { useState, type ReactElement } from 'react';
import { useOptionalPwaPhaseA } from '../../../pwa/PwaPhaseAContext';
import type { LandingLabels } from './landing-locale';
import { LandingIcon } from './LandingIcons';

const DISMISS_KEY = 'qxl-install-banner-dismissed';

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function LandingInstallBanner({ t }: { readonly t: LandingLabels }): ReactElement | null {
  const pwa = useOptionalPwaPhaseA();
  const [dismissed, setDismissed] = useState(readDismissed);
  const mode = pwa?.install.mode;
  if (
    pwa === null ||
    dismissed ||
    (mode !== 'promptable' && mode !== 'a2hs_guided' && mode !== 'installing')
  ) {
    return null;
  }
  const promptable = mode === 'promptable';
  const installing = mode === 'installing';
  return (
    <div className="qxl__install-banner" role="status">
      <span className="qxl__install-banner-icon" aria-hidden="true">
        {installing ? (
          <span className="qx-spinner qx-spinner--light" />
        ) : (
          <LandingIcon name="download" size={20} />
        )}
      </span>
      <div className="qxl__install-banner-text">
        <strong>{t.install.title}</strong>
        <small>
          {installing ? t.install.installing : promptable ? t.install.body : t.install.iosBody}
        </small>
      </div>
      {promptable ? (
        <button
          type="button"
          className="qxl__pill qxl__pill--primary qxl__install-banner-cta"
          onClick={() => {
            void pwa.promptInstall();
          }}
        >
          {t.install.cta}
        </button>
      ) : null}
      <button
        type="button"
        className="qxl__install-banner-close"
        aria-label={t.install.closeAria}
        onClick={() => {
          setDismissed(true);
          try {
            window.localStorage.setItem(DISMISS_KEY, '1');
          } catch {
            /* mod privasi — penutupan kekal untuk sesi ini sahaja */
          }
        }}
      >
        <LandingIcon name="close" size={16} />
      </button>
    </div>
  );
}
